/** Display name of this build. Change here to rename the app everywhere. */
export const APP_NAME = "BrainLog";
export const APP_TAGLINE = "Think it. Log it.";
export const DEFAULT_TIMEZONE = "Europe/Luxembourg";

function labelled<const T extends readonly string[]>(values: T, labels: Record<T[number], string>) {
  return { values, labels };
}

/** Notebooks (personal or shared notes) and research projects (with library, manuscripts, matrix). */
export const WORKSPACE_KIND = labelled(["notebook", "project"] as const, {
  notebook: "Notebook",
  project: "Project",
});
export type WorkspaceKind = (typeof WORKSPACE_KIND.values)[number];

export const TASK_STATUS = labelled(["backlog", "todo", "in_progress", "done"] as const, {
  backlog: "Backlog",
  todo: "To do",
  in_progress: "In progress",
  done: "Done",
});
export type TaskStatus = (typeof TASK_STATUS.values)[number];

export const TASK_KIND = labelled(["task", "milestone", "deliverable", "revision"] as const, {
  task: "Task",
  milestone: "Milestone",
  deliverable: "Deliverable",
  revision: "Revision",
});
export type TaskKind = (typeof TASK_KIND.values)[number];

/** Note kinds. "note" is an ordinary note; all others are typed research records. */
export const NOTE_KIND = labelled(
  [
    "note",
    "research_question",
    "hypothesis",
    "study_plan",
    "study_execution",
    "result",
    "meeting",
    "decision",
    "research_log",
    "reading_note",
    "search_log",
    "manuscript_section",
    "progress_note",
    "slides",
  ] as const,
  {
    note: "Note",
    research_question: "Research question",
    hypothesis: "Hypothesis",
    study_plan: "Study plan",
    study_execution: "Study execution",
    result: "Result",
    meeting: "Meeting",
    decision: "Decision",
    research_log: "Research log entry",
    reading_note: "Reading note",
    search_log: "Search log",
    manuscript_section: "Manuscript section",
    progress_note: "Progress note",
    slides: "Slides",
  },
);
export type NoteKind = (typeof NOTE_KIND.values)[number];
/** Typed research records (with their own fields). Ordinary notes and slides are not records. */
export const RECORD_KINDS = NOTE_KIND.values.filter((k) => k !== "note" && k !== "slides") as Exclude<NoteKind, "note" | "slides">[];
export const isRecordKind = (k: string): k is (typeof RECORD_KINDS)[number] => (RECORD_KINDS as readonly string[]).includes(k);

export const HYPOTHESIS_STATE = labelled(
  ["proposed", "under_investigation", "supported", "contradicted", "inconclusive", "retired"] as const,
  {
    proposed: "Proposed",
    under_investigation: "Under investigation",
    supported: "Supported in recorded context",
    contradicted: "Contradicted in recorded context",
    inconclusive: "Inconclusive",
    retired: "Retired",
  },
);
export type HypothesisState = (typeof HYPOTHESIS_STATE.values)[number];
/** States that are claims about evidence and therefore require linked records. */
export const EVIDENCE_BASED_HYPOTHESIS_STATES: HypothesisState[] = ["supported", "contradicted", "inconclusive"];

export const EVIDENCE_STATE = labelled(["preliminary", "checked", "superseded", "retracted"] as const, {
  preliminary: "Preliminary",
  checked: "Checked",
  superseded: "Superseded",
  retracted: "Retracted",
});
export type EvidenceState = (typeof EVIDENCE_STATE.values)[number];

export const RESULT_OUTCOME = labelled(
  ["as_expected", "negative", "null", "contradictory", "failed", "mixed", "exploratory"] as const,
  {
    as_expected: "Consistent with expectation",
    negative: "Negative",
    null: "Null",
    contradictory: "Contradictory",
    failed: "Failed run",
    mixed: "Mixed",
    exploratory: "Exploratory / no expectation",
  },
);

export const DECISION_STATUS = labelled(["idea", "proposed", "agreed", "superseded"] as const, {
  idea: "Idea (not decided)",
  proposed: "Proposed",
  agreed: "Agreed decision",
  superseded: "Superseded",
});

export const MEETING_TYPE = labelled(["supervision", "project", "internal_review", "other"] as const, {
  supervision: "Supervision",
  project: "Project meeting",
  internal_review: "Internal review",
  other: "Other",
});

export const READING_COVERAGE = labelled(["full_text", "partial_text", "abstract_only"] as const, {
  full_text: "Full text read",
  partial_text: "Partial text",
  abstract_only: "Abstract only",
});

export const READING_STATUS = labelled(["unread", "skimming", "reading", "read", "archived"] as const, {
  unread: "Unread",
  skimming: "Skimming",
  reading: "Reading",
  read: "Read",
  archived: "Archived",
});
export type ReadingStatus = (typeof READING_STATUS.values)[number];

export const REVIEW_DECISION = labelled(["candidate", "included", "excluded", "uncertain"] as const, {
  candidate: "Candidate",
  included: "Included",
  excluded: "Excluded",
  uncertain: "Uncertain",
});

export const SOURCE_TYPE = labelled(
  ["article", "book", "chapter", "preprint", "proceedings", "thesis", "report", "dataset", "software", "webpage", "other"] as const,
  {
    article: "Journal article",
    book: "Book",
    chapter: "Book chapter",
    preprint: "Preprint",
    proceedings: "Conference paper",
    thesis: "Thesis",
    report: "Report",
    dataset: "Dataset",
    software: "Software",
    webpage: "Web page",
    other: "Other",
  },
);
export type SourceType = (typeof SOURCE_TYPE.values)[number];

export const EVIDENCE_RELATION = labelled(["supports", "contradicts", "context"] as const, {
  supports: "Supports",
  contradicts: "Contradicts",
  context: "Provides context",
});
export type EvidenceRelation = (typeof EVIDENCE_RELATION.values)[number];

export const RECORD_LINK_RELATION = labelled(
  ["addresses", "tests", "plan_for", "run_of", "input", "output", "result_of", "informs", "supersedes", "related"] as const,
  {
    addresses: "Addresses question",
    tests: "Tests hypothesis",
    plan_for: "Plan for",
    run_of: "Execution of plan",
    input: "Uses input",
    output: "Produces output",
    result_of: "Result of run",
    informs: "Informs",
    supersedes: "Supersedes",
    related: "Related",
  },
);
export type RecordLinkRelation = (typeof RECORD_LINK_RELATION.values)[number];

export const MANUSCRIPT_STATUS = labelled(
  ["outline", "drafting", "internal_review", "submitted", "revision", "accepted", "rejected", "withdrawn"] as const,
  {
    outline: "Outline",
    drafting: "Drafting",
    internal_review: "Internal review",
    submitted: "Submitted",
    revision: "Revision",
    accepted: "Accepted",
    rejected: "Rejected",
    withdrawn: "Withdrawn",
  },
);
export type ManuscriptStatus = (typeof MANUSCRIPT_STATUS.values)[number];

/** Sections a new manuscript starts with (the abstract is always added first). */
export const MANUSCRIPT_SECTION_PRESETS: Record<string, string[]> = {
  article: ["Introduction", "Methods", "Results", "Discussion", "Conclusion"],
  preprint: ["Introduction", "Methods", "Results", "Discussion", "Conclusion"],
  conference_paper: ["Introduction", "Related work", "Method", "Experiments", "Results", "Discussion", "Conclusion"],
  thesis: ["Introduction", "Literature review", "Methodology", "Results", "Discussion", "Conclusion"],
  thesis_chapter: ["Introduction", "Background", "Methods", "Results", "Discussion", "Summary"],
  report: ["Executive summary", "Background", "Methods", "Findings", "Recommendations"],
  other: ["Introduction"],
};

export const MANUSCRIPT_TYPE = labelled(
  ["article", "thesis", "thesis_chapter", "report", "preprint", "conference_paper", "other"] as const,
  {
    article: "Journal article",
    thesis: "Thesis",
    thesis_chapter: "Thesis chapter",
    report: "Project report",
    preprint: "Preprint",
    conference_paper: "Conference paper",
    other: "Other",
  },
);

export const REVISION_CHANGE_STATUS = labelled(["pending", "confirmed", "declined"] as const, {
  pending: "Open",
  confirmed: "Paper changed",
  declined: "Answered without changing the paper",
});

export const EXTERNAL_REF_KIND = labelled(
  ["dataset", "repository", "environment", "script", "output", "protocol", "preregistration", "approval", "other"] as const,
  {
    dataset: "Dataset",
    repository: "Code repository",
    environment: "Environment / dependency file",
    script: "Analysis script / notebook",
    output: "Output artifact",
    protocol: "Protocol",
    preregistration: "Preregistration",
    approval: "Approval / ethics reference",
    other: "Other link",
  },
);

export const STARTING_TEMPLATE = labelled(["blank", "phd", "postdoc", "researcher"] as const, {
  blank: "Blank project",
  phd: "PhD student",
  postdoc: "Postdoctoral researcher",
  researcher: "Researcher",
});
export type StartingTemplate = (typeof STARTING_TEMPLATE.values)[number];

/** Kinds of project web page (templates.ts has what each one shows). */
export const WEB_PAGE_TEMPLATE = labelled(["summary", "funder_update", "results", "website", "scratch"] as const, {
  summary: "Project summary",
  funder_update: "Funder update",
  results: "Results",
  website: "Project website",
  scratch: "Start from scratch",
});
export type WebPageTemplate = (typeof WEB_PAGE_TEMPLATE.values)[number];

/** Looks of a project web page; pages made before looks existed are "classic". */
export const WEB_PAGE_LOOK = labelled(["classic", "modern", "minimal"] as const, {
  classic: "Classic",
  modern: "Modern",
  minimal: "Minimal",
});
export type WebPageLook = (typeof WEB_PAGE_LOOK.values)[number];

/** Sections of a project workspace browser that the user can hide. */
export const WORKSPACE_SECTIONS = labelled(
  ["overview", "knowledge", "notes", "records", "papers", "code_data", "files", "matrix", "backlog", "manuscripts", "correspondence", "review"] as const,
  {
    overview: "Overview",
    knowledge: "Knowledge base",
    notes: "Notes",
    records: "Research records",
    papers: "Papers",
    code_data: "Code and data",
    files: "Files",
    matrix: "Literature matrix",
    backlog: "Backlog",
    manuscripts: "Reports & manuscripts",
    correspondence: "Correspondence",
    review: "Weekly review",
  },
);
export type WorkspaceSection = (typeof WORKSPACE_SECTIONS.values)[number];

/** What the assistant can tell you about; each kind can go to the app, by email and to Telegram. */
export const ASSISTANT_KINDS = labelled(["ai", "papers", "readings", "references", "repos", "deadlines", "waiting", "team"] as const, {
  ai: "AI reviews and other long AI work, when finished",
  papers: "New papers for your projects",
  readings: "Readings ready",
  references: "References to check (retracted, changed)",
  repos: "Repository reports",
  deadlines: "Deadlines and milestones",
  waiting: "Emails waiting for your reply",
  team: "What others added to your projects",
});
export type AssistantKind = (typeof ASSISTANT_KINDS.values)[number];

/** Routines the assistant runs in a project once a member turns them on. */
export const ROUTINES = labelled(["literature", "reading", "references", "repos"] as const, {
  literature: "Literature watch",
  reading: "Read new papers in depth",
  references: "Reference watch",
  repos: "Repository reports",
});
export type Routine = (typeof ROUTINES.values)[number];

/** What a Knowledge base entry is about (one entry per new item of the project). */
export const KNOWLEDGE_SOURCES = labelled(["note", "task", "file", "paper", "code_data", "conversation", "email", "manuscript"] as const, {
  note: "Note",
  task: "Task",
  file: "File",
  paper: "Paper",
  code_data: "Code and data",
  conversation: "Conversation",
  email: "Email",
  manuscript: "Report or manuscript",
});
export type KnowledgeSource = (typeof KNOWLEDGE_SOURCES.values)[number];

/** What an entry of "Code and data" is. */
export const CODE_DATA_KINDS = labelled(["data", "code", "repository", "model", "other"] as const, {
  data: "Data",
  code: "Code",
  repository: "Repository",
  model: "Model",
  other: "Other",
});
export type CodeDataKind = (typeof CODE_DATA_KINDS.values)[number];

export const AI_ACTIONS = labelled(
  [
    "summarize",
    "extract_tasks",
    "explore_topic",
    "recent_updates",
    "reading_note",
    "compare_papers",
    "ask_project",
    "next_steps",
    "progress_summary",
    "build_outline",
    "draft_section",
    "review_draft",
    "check_requirements",
    "plan_revisions",
    "response_to_reviewers",
    "deep_read",
    "project_setup",
    "section_brief",
    "section_write",
    "section_edit",
    "manuscript_review",
    "revision_fix",
    "mail_triage",
    "mail_reply",
    "make_slides",
    "learn_step",
    "grill_step",
    "glossary",
    "questionnaire",
  ] as const,
  {
    summarize: "Summarize",
    extract_tasks: "Extract tasks",
    explore_topic: "Explore this topic",
    recent_updates: "Find recent updates",
    reading_note: "Create reading note",
    compare_papers: "Compare papers",
    ask_project: "Ask this project",
    next_steps: "Suggest next steps",
    progress_summary: "Summarize progress",
    build_outline: "Build outline",
    draft_section: "Draft this section",
    review_draft: "Review this draft",
    check_requirements: "Check submission requirements",
    plan_revisions: "Plan revisions",
    response_to_reviewers: "Draft response to reviewers",
    deep_read: "Go in depth",
    project_setup: "Set up from proposal",
    section_brief: "Plan this section (round 1)",
    section_write: "Write this section (round 2)",
    section_edit: "Edit with AI",
    manuscript_review: "AI review",
    revision_fix: "Fix with AI",
    mail_triage: "Find tasks and next steps in an email",
    mail_reply: "Draft a reply",
    make_slides: "Make slides",
    learn_step: "Next lesson",
    grill_step: "Next questions on the plan",
    glossary: "Build the project glossary",
    questionnaire: "Questionnaire for a co-author",
  },
);
export type AiAction = (typeof AI_ACTIONS.values)[number];

export const LIMITS = {
  noteTitleMax: 300,
  maxMarkdownImportBytes: 2 * 1024 * 1024,
  maxZipEntries: 2000,
};

/** Correspondence: how a conversation reached BrainLog. */
export const MAIL_SOURCE = labelled(["paste", "eml", "msg", "forward", "quoted", "sent"] as const, {
  paste: "Pasted",
  eml: "Email file",
  msg: "Outlook file",
  forward: "Forwarded to the address",
  quoted: "Quoted in a later message",
  sent: "Your reply",
});

export const MAIL_REPLY_TONES = labelled(["formal", "friendly", "brief"] as const, { formal: "Formal", friendly: "Friendly", brief: "Brief" });
